import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, DateTimePicker, FormRadio, NotificationDispatch, RadioGroup, TextField, Tooltip, FormToggle } from '@tryhackme/thm-ui-components';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { format, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useFormContext, Controller, useFieldArray, useWatch } from 'src/common/form';
import { Question, Task, TaskMaterial } from 'src/common/interfaces/manage-room';
import { useGetTaskImagePresignedUrlMutation, useRemoveTaskImagesMutation, useSaveTaskImageMutation, useSaveTaskMutation, useUploadTaskImagesToS3Mutation } from 'src/features/manage-room/manage-room.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { store } from 'src/app/store';
import { setIsDirty } from 'src/app/reducers/manage-room.reducer';
import { DeleteModal } from 'src/common/delete-modal';
import { ManageRoomDetail, RoomCloudDetail } from 'src/features/manage-room/manage-room.types';
import { SUMMERNOTE_TASK_OPTIONS, Summernote } from 'src/common/components/summernote';
import { TaskFormRow } from '../task-form-row';
import { StyledPlusIcon } from '../question-card/question-card.styles';
import { QuestionCard } from '../question-card';
import { DiscardChangesModal } from '../../../discard-changes-modal';
import { SaveTaskModal } from '../save-task-modal';
import { UnSavedWarnModal } from '../../../unsaved-warn-modal';
import { StyledBottomLine, StyledFormRowDescription, StyledFormRowHeading, StyledTaskFormRowContainer, StyledTaskFormRowHeadingContainer } from '../task-form-row/task-form-row.styles';
import { TaskTypeInputs, TypeSelectLabel } from './task-form.constants';
import { StyledAddQuestionButton, StyledDateSection, StyledDateTimePicker, StyledQuestionHeading, StyledRadioInputWrapper, StyledTaskButtonsContainer, StyledTooltipText, StyledTypeSelectInputWrapper } from './task-form.styles';
import { RichTextEditor } from './editor';
import { fileToBase64 } from './helpers';
import { useLocalStorage } from './hooks/use-local-storage';
type FileToUpload = {
  file: File;
  currentSrc: string;
};
const formatDateToGMT = (item: React.ReactNode) => {
  if (!item) {
    return null;
  }
  const originalDate = parseISO(item.toString());
  const zonedDate = utcToZonedTime(originalDate, 'GMT');
  const formattedDate = format(zonedDate, 'MMM do yyyy HH:mm');
  return formattedDate;
};
export const TaskForm = ({
  taskMaterialData,
  roomId,
  handleRemoveTask,
  taskNo,
  roomDetails,
  isContentDev
}: {
  taskMaterialData: Record<string, TaskMaterial[]>;
  roomId: string;
  handleRemoveTask: (taskId: string) => void;
  taskNo: number;
  roomDetails: RoomCloudDetail;
  isContentDev: boolean;
}) => {
  const imagesToUpload = useRef<FileToUpload[]>([]);
  const imagesToDelete = useRef<string[]>([]);
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState,
    reset,
    getValues
  } = useFormContext<Task>();
  const {
    fields,
    append,
    update,
    move,
    remove
  } = useFieldArray({
    control,
    name: 'infoList'
  });
  const [isSavingTask, setIsSavingTask] = useState(false);
  const {
    isDirty,
    errors
  } = formState;
  const taskId = getValues('_id') ?? '';
  const [saveTask] = useSaveTaskMutation();
  const [getPresignedUrl] = useGetTaskImagePresignedUrlMutation();
  const [uploadFileToS3] = useUploadTaskImagesToS3Mutation();
  const [saveTaskImage] = useSaveTaskImageMutation();
  const [removeTaskImages] = useRemoveTaskImagesMutation();
  const [uploadId, taskType, releaseDate, expirationDate] = useWatch({
    control,
    name: ['uploadId', 'type', 'releaseDate', 'expirationDate']
  });
  const showRemoveQuestionIcon = useMemo(() => fields.filter(f => !f.deleted).length > 1, [fields]);
  const materialBasedOnType = useMemo(() => taskMaterialData[taskType] ?? [], [taskMaterialData, taskType]);
  const selectOptions = useMemo(() => materialBasedOnType.map(item => ({
    label: item.title,
    value: item._id
  })), [materialBasedOnType]);
  const selectedTypeOption = useMemo(() => selectOptions.find(option => option.value === uploadId), [selectOptions, uploadId]);
  useEffect(() => {
    store.dispatch(setIsDirty(isDirty));
  }, [isDirty]);
  const handleDate = (type: 'releaseDate' | 'expirationDate', value: boolean) => {
    if (value) {
      setValue(type, new Date().toDateString(), {
        shouldDirty: true
      });
    } else {
      setValue(type, null, {
        shouldDirty: true
      });
    }
  };
  const handleAddQuestion = () => {
    append({
      questionNo: fields.filter(field => !field.deleted).length + 1,
      question: '<p>Update me</p>',
      answer: '',
      hint: '',
      _id: '',
      deleted: false
    });
  };
  const handleRemoveQuestion = useCallback((index: number, question: Question) => {
    if (!question._id) {
      remove(index);
      return;
    }
    update(index, {
      ...question,
      deleted: true
    });
  }, [remove, update]);
  const findQuestion = useCallback((fieldId: string) => {
    const question = fields.find(f => f.id === fieldId);
    if (question) {
      return fields.indexOf(question);
    }
    return 0;
  }, [fields]);
  const onSubmit = async (values: Task) => {
    try {
      setIsSavingTask(true);
      const imageReplacements = await Promise.all(imagesToUpload.current.map(async image => handleUploadTaskImageToS3(image)));
      let updatedDescription = values.description;
      for (const [currentSrc, newSrc] of imageReplacements) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- those are always defined as per above code
        updatedDescription = updatedDescription.replace(currentSrc!, newSrc!);
      }
      if (imagesToDelete.current.length) {
        await removeTaskImages({
          roomId,
          files: imagesToDelete.current
        }).unwrap();
      }
      const payload = {
        ...values,
        description: updatedDescription,
        roomId,
        taskId
      };
      await saveTask(payload).unwrap();
      imagesToUpload.current = [];
      imagesToDelete.current = [];
      setValue('description', updatedDescription);
      NotificationDispatch('success', 'Task successfully saved');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        // eslint-disable-next-line no-console -- should probably at least log instead of eating it
        console.error(error);
        NotificationDispatch('error', 'Something went wrong');
      }
    } finally {
      setIsSavingTask(false);
    }
  };
  const handelMoveQuestion = useCallback((dragIndex: number, atIndex: number) => {
    move(dragIndex, atIndex);
  }, [move]);
  type OnImageUpload = {
    (files: File[], insertImage: (url: string, filename?: string | Summernote.EditImageCallback) => void): void;
    (files: File[], insertImage: (base64: string, callback: (images: HTMLImageElement[]) => void) => void): void;
  };
  const handleEditorImageUpload = useCallback<NonNullable<OnImageUpload>>(async (files, insertImage) => {
    try {
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const base64 = await fileToBase64(file);
        insertImage(base64, (image: unknown) => {
          const imageNode = (image as {
            0: HTMLImageElement;
          })[0];
          imagesToUpload.current.push({
            file,
            currentSrc: imageNode.src
          });
        });
      }
    } catch {
      NotificationDispatch('error', 'Something went wrong');
    }
  }, []);
  const handleEditorMediaDelete = useCallback((images: HTMLImageElement[]) => {
    for (const image of images) {
      const src = image.getAttribute('src');
      if (!src) throw new Error('Image src is not defined');
      const url = new URL(src);
      if (url.origin === 'null') {
        imagesToUpload.current = imagesToUpload.current.filter(otherImage => src !== otherImage.currentSrc);
      } else {
        imagesToDelete.current.push(url.pathname);
      }
    }
  }, []);
  const handleUploadTaskImageToS3 = async ({
    file,
    currentSrc
  }: FileToUpload) => {
    const presignedResponse = await getPresignedUrl({
      roomId,
      fileName: file.name
    }).unwrap();
    await uploadFileToS3({
      file,
      url: presignedResponse?.url
    }).unwrap();
    const saveResponse = await saveTaskImage({
      roomId,
      files: [presignedResponse?.path]
    }).unwrap();
    const newUrl = saveResponse[0];
    if (!newUrl) throw new Error('Failed to upload image');
    return [currentSrc, newUrl];
  };
  const renderQuestionCard = useCallback((index: number, fieldId: string, question: Question) => <QuestionCard roomDetails={(roomDetails as ManageRoomDetail)} key={fieldId} index={index} fieldId={fieldId} showRemoveIcon={showRemoveQuestionIcon} handleMove={handelMoveQuestion} findQuestion={findQuestion} handleRemoveQuestion={() => handleRemoveQuestion(index, question)} isContentDev={isContentDev} />, [findQuestion, handelMoveQuestion, handleRemoveQuestion, showRemoveQuestionIcon, roomDetails, isContentDev]);
  const taskTypeInputs = useMemo(() => {
    if (roomDetails.cloud?.azure?.isEnabled) {
      return TaskTypeInputs;
    }
    return TaskTypeInputs.filter(input => input.value !== 'azure');
  }, [roomDetails]);

  // temporary solution to remember editor choice

  const {
    value: enableNewEditor,
    setValue: setEnableNewEditor
  } = useLocalStorage<boolean>('useTinyEditor', isContentDev);
  return <>
      <form>
        <TaskFormRow heading="Type" description="Set the type for your task." data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
          <Controller control={control} name="type" render={({
          field: {
            onChange,
            value
          }
        }) => <RadioGroup legend="" horizontal>
                <StyledRadioInputWrapper>
                  {taskTypeInputs.map(type => <FormRadio key={type.id} id={`${type.id}-${taskNo}`} name={type.name} label={type.label} value={type.value} onChange={onChange} checked={type.value === value} />)}
                </StyledRadioInputWrapper>
              </RadioGroup>} data-sentry-element="Controller" data-sentry-source-file="task-form.tsx" />

          <StyledTypeSelectInputWrapper data-sentry-element="StyledTypeSelectInputWrapper" data-sentry-source-file="task-form.tsx">
            {taskType !== 'none' && <Controller name="uploadId" control={control} render={({
            field: {
              onChange
            }
          }) => <TextField variant="select" isMulti={false} label={TypeSelectLabel[taskType]} selected={selectedTypeOption} isSearchable={false} options={selectOptions} handleSelection={selectedOption => {
            onChange(selectedOption?.value);
          }} />} />}
          </StyledTypeSelectInputWrapper>
        </TaskFormRow>

        <Controller name="attackBoxRequired" control={control} render={({
        field: {
          onChange,
          value
        }
      }) => <>
              <StyledTaskFormRowContainer>
                <StyledTaskFormRowHeadingContainer>
                  <StyledFormRowHeading>AttackBox or VPN enabled</StyledFormRowHeading>
                  <FormToggle id="task-attackbox-toggle" checked={value} aria-label="task-attackbox-toggle" data-testid="task-attackbox-toggle" onCheckedChange={v => onChange(v)} />
                </StyledTaskFormRowHeadingContainer>
                <StyledFormRowDescription>Does this task require the AttackBox or a VPN?</StyledFormRowDescription>
              </StyledTaskFormRowContainer>
              <StyledBottomLine />
            </>} data-sentry-element="Controller" data-sentry-source-file="task-form.tsx" />

        <TaskFormRow heading="Title" description="What do you want to call this task? This is the first thing users will see before opening your task" data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
          <TextField id={`title-${taskNo}`} {...register('title')} variant="input" label="Task title" placeholder="Week 22 - Metaspoilt" isError={Boolean(errors?.title)} data-sentry-element="TextField" data-sentry-source-file="task-form.tsx" />
        </TaskFormRow>

        <TaskFormRow heading="Objective" description="Set a goal or an objective for completing this task. Think of this as completion criteria" data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
          <TextField id={`objective-${taskNo}`} {...register('objective')} variant="input" label="Task objective" placeholder="Doing basic reconnaisance" autoComplete="off" data-sentry-element="TextField" data-sentry-source-file="task-form.tsx" />
        </TaskFormRow>

        <StyledDateSection data-sentry-element="StyledDateSection" data-sentry-source-file="task-form.tsx">
          <TaskFormRow showFieldDisableToggle heading="Release date" description="Tasks will get released instantly if release date is not set." setIsFieldDisable={value => handleDate('releaseDate', value)} fieldEnabled={Boolean(releaseDate)} data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
            <Controller control={control} name="releaseDate" render={({
            field: {
              onChange,
              value
            }
          }) => {
            const formattedReleaseDate = value ? formatDateToGMT(new Date(value).toISOString()) : null;
            return <StyledDateTimePicker>
                    {formattedReleaseDate && <Tooltip arrowPosition="bottom-center">
                        <StyledTooltipText>{`GMT: ${formattedReleaseDate}`}</StyledTooltipText>
                      </Tooltip>}
                    <DateTimePicker id={`releaseDate-${taskNo}`} disabled={!releaseDate} selected={value ? new Date(value) : null} dateFormat="MMM do yyyy HH:mm" isClearable label="Release date in local time" placeholderText="Select date" width="25.3rem" minDate={new Date()} onChange={onChange} showTimeSelect timeIntervals={15} timeFormat="HH:mm" />
                  </StyledDateTimePicker>;
          }} data-sentry-element="Controller" data-sentry-source-file="task-form.tsx" />
          </TaskFormRow>

          <TaskFormRow showFieldDisableToggle heading="End date" description="Tasks will remain available if end date is not set." setIsFieldDisable={value => handleDate('expirationDate', value)} fieldEnabled={Boolean(expirationDate)} data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
            <Controller control={control} name="expirationDate" render={({
            field: {
              onChange,
              value
            }
          }) => {
            const formattedExpirationDate = value ? formatDateToGMT(new Date(value).toISOString()) : null;
            return <StyledDateTimePicker>
                    {formattedExpirationDate && <Tooltip arrowPosition="bottom-center">
                        <StyledTooltipText>{`GMT: ${formattedExpirationDate}`}</StyledTooltipText>
                      </Tooltip>}
                    <DateTimePicker id={`expiration-${taskNo}`} disabled={!expirationDate} selected={value ? new Date(value) : null} dateFormat="MMM do yyyy HH:mm" isClearable label="End date in local time" placeholderText="Select date" width="25.3rem" minDate={new Date()} onChange={onChange} showTimeSelect timeIntervals={15} timeFormat="HH:mm" />
                  </StyledDateTimePicker>;
          }} data-sentry-element="Controller" data-sentry-source-file="task-form.tsx" />
          </TaskFormRow>
        </StyledDateSection>
        <StyledBottomLine data-sentry-element="StyledBottomLine" data-sentry-source-file="task-form.tsx" />
        <TaskFormRow heading="Task Content" description={`This field contains the task's body (text, animation, images, and code snippets, among other things).`} data-sentry-element="TaskFormRow" data-sentry-source-file="task-form.tsx">
          {isContentDev && <StyledTaskFormRowContainer>
              <StyledTaskFormRowHeadingContainer>
                <StyledFormRowHeading>Enable the new task editor experience </StyledFormRowHeading>
                <FormToggle checked={Boolean(enableNewEditor)} aria-label="enable new editor toggle" data-testid="enable-new-editor" onCheckedChange={setEnableNewEditor} />
              </StyledTaskFormRowHeadingContainer>
              <StyledFormRowDescription>
                Try out the new task editor that supports markdown and other new features
              </StyledFormRowDescription>
            </StyledTaskFormRowContainer>}
          {enableNewEditor ? <RichTextEditor content={getValues('description')} onChange={content => setValue('description', content, {
          shouldDirty: true
        })} onImageUpload={handleEditorImageUpload} onMediaDelete={handleEditorMediaDelete} /> : <Summernote options={SUMMERNOTE_TASK_OPTIONS} {...register('description')} onImageUpload={handleEditorImageUpload} onMediaDelete={handleEditorMediaDelete} showLegacyCodeButton />}
        </TaskFormRow>

        <StyledQuestionHeading data-sentry-element="StyledQuestionHeading" data-sentry-source-file="task-form.tsx">Questions, answers & hints</StyledQuestionHeading>

        {fields.map((field, index) => {
        if (field.deleted) return null;
        return renderQuestionCard(index, field.id, field);
      })}

        <StyledAddQuestionButton variant="outlined" color="secondary" onClick={handleAddQuestion} data-sentry-element="StyledAddQuestionButton" data-sentry-source-file="task-form.tsx">
          <StyledPlusIcon icon={faPlus} data-sentry-element="StyledPlusIcon" data-sentry-source-file="task-form.tsx" />
          Add question
        </StyledAddQuestionButton>

        <StyledTaskButtonsContainer data-sentry-element="StyledTaskButtonsContainer" data-sentry-source-file="task-form.tsx">
          {isDirty && <DiscardChangesModal handleActionButton={() => {
          reset();
        }} />}

          <SaveTaskModal isSavingChanges={isSavingTask} handleAction={handleSubmit(onSubmit)} isSaveButtonEnabled={!isDirty} data-sentry-element="SaveTaskModal" data-sentry-source-file="task-form.tsx" />

          <DeleteModal activeIndex={0} modalTitleText="Delete Task" triggerElement={<Button color="error">Delete task</Button>} modalBodyText="Are you sure you want delete this task?" actionButtonText="Delete task" showDeleteWarningBox deleteWarningBoxText="Once it is deleted, a task cannot be recovered." handleDeleteAction={() => {
          store.dispatch(setIsDirty(false));
          handleRemoveTask(taskId);
        }} data-sentry-element="DeleteModal" data-sentry-source-file="task-form.tsx" />
        </StyledTaskButtonsContainer>
      </form>

      <UnSavedWarnModal isSavingChanges={isSavingTask}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleSubmit(onSubmit)} customWarningMessage={<>
            You have made changes but you are about to leave this task? <br />
            Do you want to save your changes and update this room?
          </>} handleDiscardChanges={() => reset()} data-sentry-element="UnSavedWarnModal" data-sentry-source-file="task-form.tsx" />
    </>;
};